import { Link } from 'react-router-dom';
import { CONTACT_INFO, SOCIAL_LINKS } from '@/lib/data';
import { useSettings } from '@/lib/useSettings';
import { formatWorkingHours } from '@/lib/workingHours';
import { MapPin, Clock, Youtube, Instagram, Facebook } from 'lucide-react';

export const Footer = () => {
  const settings = useSettings();
  const CONTACT_INFO = settings.contact || {};
  const SOCIAL_LINKS = settings.social || {};
  return (
    <footer className="bg-[#1F2937] text-white" data-testid="footer">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-10 py-12 sm:py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-10">
          {/* Brand */}
          <div className="sm:col-span-2 lg:col-span-1">
            <div className="flex items-center gap-2 mb-4">
              <img src={settings.doctor?.image || 'https://customer-assets.emergentagent.com/job_dr-nilufershop/artifacts/rd2kgze0_image.png'} alt="Prof. Dr. Nilüfer Yıldırım" className="w-9 h-9 rounded-full object-cover" />
              <div>
                <p className="font-heading font-semibold text-sm">Prof. Dr. Nilüfer Yıldırım</p>
                <p className="text-xs text-gray-400">Bütüncül Sağlık &amp; Fonksiyonel Tıp</p>
              </div>
            </div>
            <p className="text-sm text-gray-400 leading-relaxed max-w-xs">
              Sağlığınıza bütüncül yaklaşım, kişiye özel çözümler.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-heading font-semibold text-sm mb-4">Sayfalar</h3>
            <nav className="space-y-2">
              {[
                { href: '/', label: 'Anasayfa' },
                { href: '/hizmetler', label: 'Tedaviler' },
                { href: '/surec', label: 'Tedavi Süreci' },
                { href: '/hakkimizda', label: 'Hakkımda' },
                { href: '/haberler', label: 'Haberler' },
                { href: '/icerikler', label: 'İçerikler' },
                { href: '/iletisim', label: 'İletişim' },
                { href: '/danismanlik', label: 'Sade Düşün' },
              ].map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className="block text-sm text-gray-300 hover:text-white transition-colors py-1"
                >
                  {link.label}
                </Link>
              ))}
            </nav>
          </div>

          {/* Contact */}
          <div>
            <h3 className="font-heading font-semibold text-sm mb-4">İletişim</h3>
            <div className="space-y-3 text-sm text-gray-300">
              <div className="flex items-start gap-2">
                <MapPin size={16} className="mt-0.5 shrink-0 text-[#D8C7A6]" />
                <span>{CONTACT_INFO.address}</span>
              </div>
              <div className="flex items-start gap-2">
                <Clock size={16} className="mt-0.5 shrink-0 text-[#D8C7A6]" />
                <div>
                  {CONTACT_INFO.schedule ? (
                    formatWorkingHours(CONTACT_INFO.schedule)?.map((g, i) => (
                      <p key={i}>
                        {g.days.length > 1 ? `${g.days[0]} – ${g.days[g.days.length - 1]}` : g.days[0]}
                        {': '}
                        {g.open ? `${g.start} – ${g.end}` : 'Kapalı'}
                      </p>
                    ))
                  ) : (
                    <>
                      <p>{CONTACT_INFO.hours}</p>
                      <p>{CONTACT_INFO.hours_saturday}</p>
                      <p>{CONTACT_INFO.hours_sunday}</p>
                    </>
                  )}
                </div>
              </div>
              <a
                href={CONTACT_INFO.map_link}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center py-1 text-[#5BBFCC] hover:text-white transition-colors text-xs font-medium"
              >
                Yol Tarifi Al &rarr;
              </a>
            </div>
          </div>

          {/* Social + Legal */}
          <div>
            <h3 className="font-heading font-semibold text-sm mb-4">Sosyal Medya</h3>
            <div className="flex gap-3 mb-6">
              <a href={SOCIAL_LINKS.youtube} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#1F6E7A] transition-colors" aria-label="YouTube">
                <Youtube size={16} />
              </a>
              <a href={SOCIAL_LINKS.instagram} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#1F6E7A] transition-colors" aria-label="Instagram">
                <Instagram size={16} />
              </a>
              <a href={SOCIAL_LINKS.facebook} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#1F6E7A] transition-colors" aria-label="Facebook">
                <Facebook size={16} />
              </a>
            </div>
            <div className="space-y-2">
              <Link to="/kvkk" className="block text-xs text-gray-400 hover:text-gray-200 transition-colors py-1">
                KVKK Aydınlatma Metni
              </Link>
              <Link to="/kvkk" className="block text-xs text-gray-400 hover:text-gray-200 transition-colors py-1">
                Çerez Politikası
              </Link>
            </div>
          </div>
        </div>

        <div className="mt-10 pt-6 border-t border-gray-700/50 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-gray-400">
          <span>&copy; {new Date().getFullYear()} Prof. Dr. Nilüfer Yıldırım. Tüm hakları saklıdır.</span>
          <a href="https://mudamind.com" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors" data-testid="footer-mudamind">
            <span>Developed by</span>
            <img src="https://customer-assets.emergentagent.com/job_e4216fc8-d3f2-4251-8d26-073baee657e4/artifacts/7npuab9t_ana%20logo%20arka%20plans%C4%B1z.png" alt="Muda Mind Analytics" className="h-5 w-5" />
            <span className="font-medium text-gray-300">Muda Mind Analytics</span>
          </a>
        </div>
      </div>
    </footer>
  );
};
